import type { SingleLight } from "building-types/electrical/electricalLayoutTypes";
import { ElectricalLayoutType, RoomBoundaryType } from "building-types/electrical/electricalLayoutTypesEnum";
import { ControllerTypeSlot, LoadTypeSlot } from "building-types/electrical/electricalSlots";
import {
  ControllerSelectionType,
  ControllerSwitchType,
  ControllerType,
  LoadType,
} from "building-types/electrical/electricalTypesEnum";
import type { Controller, GeneratedElectricalGroup, Load } from "building-types/electrical/generatedElectricalTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { getLoadCatalog } from "doc/getLoadCatalog";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { poleOfInaccessibilityPolyLine2 } from "geom/label";
import { polyLine2Centroid, polyLine2Copy, polyLine2IsClosedRect, polyLine2MergeCollinearEdges } from "geom/polyLine2";
import { polyLine2MaximalRectangle } from "geom/polyLine2MaximalRect";
import type { Vec2 } from "geom/vec2";
import { vec2Add, vec2Copy } from "geom/vec2";
import { commitControllerSelection } from "../controllerSelection/controllerSelectionCommit";
import { controllerTypeFromSlots } from "../controllerTypeFromSlots";
import type { ElectricalLayoutFunc, ElectricalLayoutOpts } from "../electricalLayoutTypes";
import { loadTypeFromSlots } from "../loadTypeFromSlots";

export function roomSingleLightWithOptionalFan({
  layoutType: type,
  addCeilingFan: addCeilingFan = false,
  addBathFan: addBathFan = false,
  controllerPlacementType = [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  controllerSlot = ControllerTypeSlot.PRIMARY,
  loadSlot = LoadTypeSlot.PRIMARY_LIGHT,
  loadType,
  singleController = false,
  roomBoundaryType,
}: SingleLight): ElectricalLayoutFunc {
  const resolvedRoomBoundaryType = roomBoundaryType === undefined ? RoomBoundaryType.BOUNDING_BOX : roomBoundaryType;

  return async (opts: ElectricalLayoutOpts) => {
    const { room, controllerSelectionFunc, elecSlots, roomOutlines, electricalItemCatalogSettings } = opts;
    const resolvedLoadType = loadType ?? loadTypeFromSlots(loadSlot, elecSlots);
    const resolvedControllerType = controllerTypeFromSlots(controllerSlot, elecSlots);
    const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
    const loadDescriptor = loadCatalog[resolvedLoadType];

    const location: Vec2 = [];

    const insideFramingOutline = roomOutlines.INSIDE_OF_MASS_IGNORE_VOID;
    const outline = polyLine2MergeCollinearEdges(polyLine2Copy(ensureDefined(insideFramingOutline)));

    let pX: number;
    let pY: number;
    if (resolvedRoomBoundaryType === RoomBoundaryType.MAX_RECTANGLE) {
      const { maxRectAsPolyLine2 } = polyLine2MaximalRectangle(outline);
      [pX, pY] = ensureDefined(polyLine2Centroid(maxRectAsPolyLine2));
    } else {
      if (polyLine2IsClosedRect(outline)) {
        [pX, pY] = ensureDefined(polyLine2Centroid(outline));
      } else {
        [pX, pY] = ensureDefined(poleOfInaccessibilityPolyLine2(outline));
      }
    }

    location.push(pX);
    location.push(pY);

    const candidate = await controllerSelectionFunc(controllerPlacementType, [pX, pY]);
    if (!candidate) {
      return [];
    }
    const lightControllerPosition = commitControllerSelection(candidate, type, resolvedControllerType, [pX, pY]);

    const resolvedLoad: Load = {
      loadType: resolvedLoadType,
      loadDescriptor,
      position: vec2Copy(location),
      normal: [1, 0],
      levelId: room.level.id,
      sourceRoomHandle: room.roomHandle,
    };

    const resolvedController: Controller = {
      type: ControllerType.SWITCH,
      switchType: ControllerSwitchType.STANDARD,
      position: lightControllerPosition,
      loadIndex: 0,
      candidateId: candidate?.candidate?.id,
      levelId: room.level.id,
      sourceRoomHandle: room.roomHandle,
      labels: makeBuiltInLabelTable("addToElectricalPlan"),
    };

    const groups: GeneratedElectricalGroup[] = [];

    const lightElectricalGroup: GeneratedElectricalGroup = {
      levelId: opts.room.level.id,
      loads: [resolvedLoad],
      controllers: [resolvedController],
    };

    groups.push(lightElectricalGroup);

    if (resolvedLoadType === LoadType.CEILING_FAN_LIGHT) {
      const virtualLoad = {
        ...resolvedLoad,
        loadType: LoadType.VIRTUAL_CEILING_FAN_LIGHT,
        loadDescriptor: loadCatalog[LoadType.VIRTUAL_CEILING_FAN_LIGHT],
      };
      const virtualFanControllerPosition = commitControllerSelection(candidate, type, resolvedControllerType, [pX, pY]);
      const fanGroup: GeneratedElectricalGroup = {
        levelId: opts.room.level.id,
        loads: [virtualLoad],
        controllers: [{ ...resolvedController, position: virtualFanControllerPosition }],
      };
      groups.push(fanGroup);
    }

    let fanLocation = vec2Copy(location);

    if (!addCeilingFan && !addBathFan) {
      return groups;
    }

    let fanType = elecSlots.loads.fanCeiling;

    if (addBathFan) {
      fanType = elecSlots.loads.bathFan;
      fanLocation = vec2Add(location[0], location[1], 0, 6);
    }

    const fanCandidate = await controllerSelectionFunc(controllerPlacementType, [pX, pY]);
    if (!fanCandidate) {
      // console.warn("no fan controller assigned");
      return [];
    }
    const fanControllerPosition = commitControllerSelection(
      fanCandidate,
      ElectricalLayoutType.SINGLE_LIGHT,
      resolvedControllerType,
      [pX, pY],
    );

    const finalController = !singleController ? fanControllerPosition : lightControllerPosition;

    const fanElectricalGroup: GeneratedElectricalGroup = {
      levelId: opts.room.level.id,
      loads: [
        {
          loadType: fanType,
          loadDescriptor: loadCatalog[fanType],
          position: vec2Copy(fanLocation),
          normal: [1, 0],
          levelId: room.level.id,
          sourceRoomHandle: room.roomHandle,
        },
      ],
      controllers: [
        {
          type: ControllerType.SWITCH,
          switchType: ControllerSwitchType.STANDARD,
          position: finalController,
          loadIndex: 0,
          levelId: room.level.id,
          candidateId: candidate?.candidate?.id,
          sourceRoomHandle: room.roomHandle,
          labels: makeBuiltInLabelTable("addToElectricalPlan"),
        },
      ],
    };

    return [...groups, fanElectricalGroup];
  };
}
